import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, AlertTriangle, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import api from '../../lib/api';
import { formatImporte } from '../../lib/utils';

const NOMBRE = { edemsa: 'Edemsa', agua: 'Agua', tasa_municipal: 'Tasa municipal' };
const mesLabel = (p) => { const [y, m] = (p || '').split('-'); return y ? `${m}/${y}` : ''; };

export const CargaFacturasServicios = ({ edificioId, periodo, onDone }) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [res, setRes] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const elegir = (list) => setFiles(Array.from(list || []).filter(f => f.name.toLowerCase().endsWith('.pdf')));
  const cerrar = (o) => { setOpen(o); if (!o) { setFiles([]); setRes(null); } };

  const subir = async () => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await api.post(`/egresos/${edificioId}/facturas-servicios`, fd, { params: { periodo }, timeout: 300000, headers: { 'Content-Type': 'multipart/form-data' } });
      setRes(r.data);
      if (r.data.cargadas.length) { toast.success(`${r.data.cargadas.length} unidad(es) cargadas`); onDone?.(); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudieron cargar las facturas');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8" onClick={() => setOpen(true)} data-testid="carga-facturas-servicios-btn">
        <Receipt className="w-4 h-4 mr-1.5" /> Carga de facturas servicios
      </Button>
      <Dialog open={open} onOpenChange={cerrar}>
        <DialogContent className="max-w-xl" data-testid="carga-facturas-servicios-dialog">
          <DialogHeader>
            <DialogTitle>Carga de facturas de servicios</DialogTitle>
            <DialogDescription>Subí todos los PDF juntos. La app lee cada factura, la asigna a la unidad por su NIC y carga cada cuota en el mes en que vence (este mes y el siguiente; las cuotas de meses anteriores se omiten). Los meses ya aprobados no se modifican. Edemsa, Agua (Aysam) y Tasa municipal.</DialogDescription>
          </DialogHeader>
          {!res ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); elegir(e.dataTransfer.files); }}
              onClick={() => inputRef.current?.click()}
              className={`rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${drag ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:bg-slate-50'}`}
              data-testid="carga-facturas-dropzone"
            >
              <Upload className="w-8 h-8 mx-auto text-slate-400" />
              <p className="mt-2 text-sm text-slate-700">{files.length ? `${files.length} PDF listos para cargar` : 'Arrastrá los PDF acá o tocá para elegirlos'}</p>
              <input ref={inputRef} type="file" accept="application/pdf" multiple className="hidden"
                onChange={(e) => elegir(e.target.files)} data-testid="carga-facturas-input" />
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto text-sm" data-testid="carga-facturas-resultado">
              {res.cargadas.map((c, i) => (
                <div key={i} className="flex items-start gap-2 rounded bg-emerald-50 px-3 py-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <b>{c.unidad_nombre}</b> · {NOMBRE[c.servicio]} {mesLabel(c.periodo)}: $ {formatImporte(c.monto, 2)}
                    {c.valor_anterior ? <span className="block text-xs text-slate-500">Reemplazó $ {formatImporte(c.valor_anterior, 2)}</span> : null}
                    {c.detalle && <span className="block text-xs text-slate-500">{c.detalle}</span>}
                  </div>
                </div>
              ))}
              {[...(res.avisos || []), ...res.errores].map((e, i) => (
                <div key={`e${i}`} className="flex items-start gap-2 rounded bg-amber-50 px-3 py-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div><b>{e.archivo}</b><span className="block text-xs text-amber-800">{e.motivo}</span></div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            {!res ? (
              <Button onClick={subir} disabled={!files.length || subiendo} data-testid="carga-facturas-subir">
                {subiendo ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Leyendo facturas…</> : 'Cargar facturas'}
              </Button>
            ) : (
              <Button onClick={() => cerrar(false)} data-testid="carga-facturas-cerrar">Listo</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
